import React, { useState, useEffect } from "react";

export default function GroupsEdit() {
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [groupMembers, setGroupMembers] = useState({}); // { groupId: Set of memberIds }
  const [loading, setLoading] = useState(true);

  // Fetch all groups and members on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, membersRes, gmRes] = await Promise.all([
          fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups"),
          fetch(process.env.REACT_APP_BACKEND_URL+"/report_members"),
          fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups_members")
        ]);

        const groupsData = await groupsRes.json();
        const membersData = await membersRes.json();
        const gmData = await gmRes.json();

        setGroups(groupsData || []);
        setMembers(membersData || []);

        // Transform group_members array into dictionary { groupId: Set(memberIds) }
        const gmDict = {};
        (gmData || []).forEach(gm => {
          if (!gmDict[gm.group_id]) gmDict[gm.group_id] = new Set();
          gmDict[gm.group_id].add(gm.member_id);
        });
        setGroupMembers(gmDict);

      } catch (err) {
        console.error("Failed to fetch group data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <p>Loading groups...</p>;

  // Handle toggling group active state
  const toggleActive = async (group) => {
    const updated = { ...group, is_active: group.is_active ? 0 : 1 };
    await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups/${group.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: updated.is_active })
    });
    setGroups(prev => prev.map(g => g.id === group.id ? updated : g));
  };

  // Handle toggling member in group
  const toggleMember = async (groupId, memberId) => {
    const currentSet = new Set(groupMembers[groupId] || []);
    const hasMember = currentSet.has(memberId);

    if (hasMember) {
      // Remove member
      await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups_members/${groupId}/${memberId}`, {
        method: "DELETE"
      });
      currentSet.delete(memberId);
    } else {
      // Add member
      await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups_members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: groupId, member_id: memberId })
      });
      currentSet.add(memberId);
    }

    setGroupMembers(prev => ({ ...prev, [groupId]: currentSet }));
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Group Management</h2>

      {groups.map(group => (
        <div key={group.id} className="mb-6 border rounded p-4 shadow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{group.name}</h3>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!group.is_active}
                onChange={() => toggleActive(group)}
              />
              Active
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {members.map(member => {
              const checked = groupMembers[group.id]?.has(member.id) || false;
              return (
                <label key={member.id} className="flex items-center gap-2 border p-2 rounded hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMember(group.id, member.id)}
                  />
                  {member.name} {member.surname || ""}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
